// @mui material components
import Card from "@mui/material/Card";
import React from "react";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import LockIcon from "@mui/icons-material/Lock";
import { VscEye, VscEyeClosed } from "react-icons/vsc";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Link, useNavigate } from "react-router-dom";
import headerBg from "assets/images/curved-images/Header.png";
import { toast } from "react-toastify";
import api from "store/api/api";
import { List, ListItem } from "@mui/material";
import formatStringWithSpaces from "services/stringconverter";

function Settings() {
  const navigate = useNavigate();

  const [form, setForm] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("password");

  React.useEffect(() => {
    const loginData = JSON.parse(localStorage.getItem("loginData"));
    if (!loginData) {
      navigate("/authentication/sign-in");
    }
  }, [navigate]);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const toggleNewPasswordVisibility = () =>
    setShowNewPassword(!showNewPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const toggleCurrentPasswordVisibility = () =>
    setShowCurrentPassword(!showCurrentPassword);

  const validateForm = () => {
    const requiredFields = {
      "Current password": form.currentPassword,
      "New password": form.newPassword,
      "Confirm password": form.confirmPassword,
    };

    const emptyField = Object.entries(requiredFields).find(
      ([_, value]) => !value
    );
    if (emptyField) {
      toast.error(`${emptyField[0]} is required`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      if (form.newPassword === form.confirmPassword) {
        const data = {
          password: form.currentPassword,
          new_password: form.newPassword,
        };
        const response = await api.changePassword(data);
        if (response.status === 1) {
          toast.success(formatStringWithSpaces(response.message));
          setForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
        } else {
          toast.error(formatStringWithSpaces(response.message));
        }
      } else {
        toast.error("New password and confirm password do not match");
      }
    } catch (error) {
      console.error("Failed to change password:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pt={1}>
        <SoftBox mb={3}>
          <Card sx={{ minHeight: "calc(100vh - 110px)" }}>
            <SoftBox
              sx={{
                backgroundColor: "primary.main",
                backgroundImage: `url(${headerBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={{ xs: 1, md: 3 }}
              py={2.5}
            >
              <SoftTypography color="white" variant="h6" fontSize="20px">
                Settings
              </SoftTypography>
            </SoftBox>
            <SoftBox pl={3} pr={4} py={2} display="flex" flexDirection="column">
              <SoftBox
                mb={2}
                display="flex"
                sx={{
                  flexDirection:{
                    xs:"column",
                    sm:"row"
                  }
                }}
                gap={2}
              >
                <SoftTypography
                  variant="h6"
                  sx={{
                    padding: "4px 12px",
                    fontSize: "16px",
                    width: "200px",
                    textAlign: "center",
                    alignSelf: "center",
                    borderRadius: "20px",
                    border: "0.5px solid",
                    borderColor: "border.main",
                    cursor: "pointer",
                    fontWeight: activeTab === "password" ? "bold" : "regular",
                    backgroundColor:
                      activeTab === "password" ? "info.main" : "white.main",
                    color:
                      activeTab === "password" ? "white.main" : "text.main",
                  }}
                  onClick={() => setActiveTab("password")}
                >
                  Change Password
                </SoftTypography>

                <SoftTypography
                  variant="h6"
                  sx={{
                    padding: "4px 12px",
                    fontSize: "16px",
                    width: "200px",
                    textAlign: "center",
                    alignSelf: "center",
                    borderRadius: "20px",
                    border: "0.5px solid",
                    borderColor: "border.main",
                    cursor: "pointer",
                    fontWeight: activeTab === "privacy" ? "bold" : "regular",
                    backgroundColor:
                      activeTab === "privacy" ? "info.main" : "white.main",
                    color: activeTab === "privacy" ? "white.main" : "text.main",
                  }}
                  onClick={() => setActiveTab("privacy")}
                >
                  Privacy Policy
                </SoftTypography>

                <SoftTypography
                  variant="h6"
                  sx={{
                    padding: "4px 12px",
                    fontSize: "16px",
                    width: "200px",
                    textAlign: "center",
                    alignSelf: "center",
                    borderRadius: "20px",
                    border: "0.5px solid",
                    borderColor: "border.main",
                    cursor: "pointer",
                    fontWeight: activeTab === "terms" ? "bold" : "regular",
                    backgroundColor:
                      activeTab === "terms" ? "info.main" : "white.main",
                    color: activeTab === "terms" ? "white.main" : "text.main",
                  }}
                  onClick={() => setActiveTab("terms")}
                >
                  Terms & Conditions
                </SoftTypography>
              </SoftBox>

              {activeTab === "password" ? (
                <SoftBox
                  sx={{ height: "calc(100vh - 265px)", overflowY: "auto" }}
                >
                  <SoftTypography
                    fontSize="16px"
                    color="text"
                    mt={2}
                    width={{ xs: "100%", md: "70%" }}
                  >
                    To keep your account secure, please enter your current
                    password followed by a new password. Make sure the new
                    password meets the required criteria.
                  </SoftTypography>
                  <SoftBox sx={{ pb: { xs: 4, md: 8 } }}>
                    <SoftBox p={{ md: 3 }} sx={{ maxWidth: "430px" }}>
                      <SoftBox mb={2} sx={{ position: "relative" }}>
                        <SoftInput
                          value={form.currentPassword}
                          name="currentPassword"
                          onChange={handleChange}
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder="Current Password"
                          icon={{ component: <LockIcon />, direction: "left" }}
                          sx={{
                            padding: "16px 40px 16px 4px",
                            "&.Mui-focused": {
                              padding: "16px 40px 16px 4px",
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: "#737373 !important",
                              fontSize: "16px",
                              opacity: 1,
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "16px",
                            },
                          }}
                        />
                        <SoftBox
                          onClick={toggleCurrentPasswordVisibility}
                          sx={{
                            position: "absolute",
                            right: "12px",
                            top: "55%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "text.main",
                          }}
                        >
                          {showCurrentPassword ? (
                            <VscEyeClosed size={20} />
                          ) : (
                            <VscEye size={20} />
                          )}
                        </SoftBox>
                      </SoftBox>

                      <SoftBox
                        sx={{
                          borderTop: "0.5px solid transparent",
                          borderColor: "border.main",
                          my: 3.4,
                          width: "100%",
                          borderImage:
                            "repeating-linear-gradient(90deg, transparent, transparent 5px, currentColor 5px, currentColor 15px) 1",
                        }}
                      />

                      <SoftBox mb={2} sx={{ position: "relative" }}>
                        <SoftInput
                          value={form.newPassword}
                          name="newPassword"
                          onChange={handleChange}
                          type={showNewPassword ? "text" : "password"}
                          placeholder="New Password"
                          icon={{ component: <LockIcon />, direction: "left" }}
                          sx={{
                            padding: "16px 40px 16px 4px",
                            "&.Mui-focused": {
                              padding: "16px 40px 16px 4px",
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: "#737373 !important",
                              fontSize: "16px",
                              opacity: 1,
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "16px",
                            },
                          }}
                        />
                        <SoftBox
                          onClick={toggleNewPasswordVisibility}
                          sx={{
                            position: "absolute",
                            right: "12px",
                            top: "55%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "text.main",
                          }}
                        >
                          {showNewPassword ? (
                            <VscEyeClosed size={20} />
                          ) : (
                            <VscEye size={20} />
                          )}
                        </SoftBox>
                      </SoftBox>

                      <SoftBox mb={2} sx={{ position: "relative" }}>
                        <SoftInput
                          value={form.confirmPassword}
                          name="confirmPassword"
                          onChange={handleChange}
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm Password"
                          icon={{ component: <LockIcon />, direction: "left" }}
                          sx={{
                            padding: "16px 40px 16px 4px",
                            "&.Mui-focused": {
                              padding: "16px 40px 16px 4px",
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: "#737373 !important",
                              fontSize: "16px",
                              opacity: 1,
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "16px",
                            },
                          }}
                        />
                        <SoftBox
                          onClick={toggleConfirmPasswordVisibility}
                          sx={{
                            position: "absolute",
                            right: "12px",
                            top: "55%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "text.main",
                          }}
                        >
                          {showConfirmPassword ? (
                            <VscEyeClosed size={20} />
                          ) : (
                            <VscEye size={20} />
                          )}
                        </SoftBox>
                      </SoftBox>

                      <SoftButton
                        variant="gradient"
                        color="primary"
                        fullWidth
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                          padding: "14px 24px",
                          mt: 1,
                          width: "100%",
                          fontSize: "14px",
                        }}
                      >
                        {isSubmitting ? "Saving..." : "Save Changes"}
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              ) : activeTab === "privacy" ? (
                <SoftBox
                  sx={{ height: "calc(100vh - 265px)", overflowY: "auto" }}
                >
                  <SoftBox>
                    <SoftBox>
                      <Card sx={{ minHeight: "100vh" }}>
                        <SoftBox sx={{ p: 2 }}>
                          <SoftTypography variant="body1">
                            <strong>Effective Date:</strong>
                            <span style={{ fontWeight: 400 }}>
                              {" "}
                              April, 2025
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 2 }}>
                            <strong>G2 Web Development LLC</strong>
                            <span style={{ fontWeight: 400 }}>
                              {" "}
                              ("we," "our," or "us") respects your privacy and
                              is committed to protecting it through this Privacy
                              Policy.
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 2 }}>
                            <span style={{ fontWeight: 400 }}>
                              This Privacy Policy explains how the CC&amp;I
                              Field Reporting app ("App") collects, uses, and
                              protects your information.
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 3 }}>
                            <strong>1. Information We Collect</strong>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 1 }}>
                            <span style={{ fontWeight: 400 }}>
                              We may collect the following information when you
                              use the App:
                            </span>
                          </SoftTypography>

                          <List sx={{ pl: 4 }}>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Inspection reports entered by users (including
                              text, images, and form data)
                            </ListItem>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Project details and site contact information
                            </ListItem>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              User account information (name, email, role)
                            </ListItem>
                          </List>

                          <SoftTypography variant="body1">
                            <span style={{ fontWeight: 400 }}>
                              The App does not collect personal financial
                              information, health information, or personal
                              device information beyond what is necessary for
                              inspection reporting.
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 3 }}>
                            <strong>2. How We Use Information</strong>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 1 }}>
                            <span style={{ fontWeight: 400 }}>
                              We use collected information to:
                            </span>
                          </SoftTypography>

                          <List sx={{ pl: 4 }}>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Enable the creation, editing, review, and approval
                              of field inspection reports
                            </ListItem>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Facilitate syncing of inspection reports to cloud
                              services when available
                            </ListItem>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Manage user roles and access rights within the App
                              and web dashboard
                            </ListItem>
                          </List>

                          <SoftTypography variant="body1">
                            <span style={{ fontWeight: 400 }}>
                              We do not sell, rent, or share personal
                              information with third parties for marketing
                              purposes.
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 3 }}>
                            <strong>3. Data Security</strong>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 1 }}>
                            <span style={{ fontWeight: 400 }}>
                              We implement appropriate security measures to
                              protect your information. However, no electronic
                              storage or transmission is 100% secure.
                            </span>
                            <br />
                            <span style={{ fontWeight: 400 }}>
                              We encourage users to safeguard their login
                              credentials and device access.
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 3 }}>
                            <strong>4. User Rights</strong>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 1 }}>
                            <span style={{ fontWeight: 400 }}>
                              Users may request:
                            </span>
                          </SoftTypography>

                          <List sx={{ pl: 4 }}>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Access to the information collected about them
                            </ListItem>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Correction of any inaccurate information
                            </ListItem>
                            <ListItem
                              sx={{ display: "list-item", fontWeight: 400 }}
                            >
                              Deletion of their user account (subject to company
                              approval policies)
                            </ListItem>
                          </List>

                          <SoftTypography variant="body1">
                            <span style={{ fontWeight: 400 }}>
                              Requests can be made by contacting our support
                              team (contact information below).
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 3 }}>
                            <strong>5. Changes to This Policy</strong>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 1 }}>
                            <span style={{ fontWeight: 400 }}>
                              We may update this Privacy Policy from time to
                              time. Updates will be posted on our Support page.
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 3 }}>
                            <strong>6. Contact Us</strong>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 1 }}>
                            <span style={{ fontWeight: 400 }}>
                              If you have any questions about this Privacy
                              Policy or your information, contact:
                            </span>
                          </SoftTypography>

                          <SoftTypography variant="body1" sx={{ mt: 1 }}>
                            <strong>G2 Web Development LLC</strong>
                            <br />
                            <span style={{ fontWeight: 400 }}>
                              Email: info@g2webdevelopment.com
                            </span>
                            <br />
                            <span style={{ fontWeight: 400 }}>
                              Phone: +1(864)561-5380
                            </span>
                            <br />
                            <span style={{ fontWeight: 400 }}>
                              Website:{" "}
                              <a
                                href="https://g2webdevelopment.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                https://g2webdevelopment.com
                              </a>
                            </span>
                          </SoftTypography>
                        </SoftBox>
                      </Card>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              ) : (
                <SoftBox
                  sx={{ height: "calc(100vh - 265px)", overflowY: "auto" }}
                >
                  <SoftBox>
                    <SoftBox p={3}>
                      <SoftTypography variant="body1">
                        <strong>Effective Date:</strong>
                        <span style={{ fontWeight: 400 }}> April, 2025</span>
                      </SoftTypography>

                      <SoftTypography paragraph>
                        These Terms and Conditions (“Terms”) govern your access
                        to and use of the CC&I Field Reporting mobile
                        application (“App”) and related services provided by{" "}
                        <strong>G2 Web Development LLC</strong> (“we,” “us,” or
                        “our”).
                      </SoftTypography>

                      <SoftTypography paragraph>
                        By accessing or using the App, you agree to be bound by
                        these Terms.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        1. Use of the App
                      </SoftTypography>
                      <SoftTypography paragraph>
                        The CC&I Field Reporting App is intended for authorized
                        users within companies using the App for internal field
                        inspection and reporting purposes. You agree to use the
                        App only for lawful purposes and in accordance with
                        these Terms.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        2. User Accounts
                      </SoftTypography>
                      <SoftTypography paragraph>
                        Access to certain features may require creating a user
                        account. You are responsible for maintaining the
                        confidentiality of your account information and for all
                        activities under your account.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        3. Data and Content
                      </SoftTypography>
                      <SoftTypography paragraph>
                        You are responsible for any content you input into the
                        App, including inspection data, images, and project
                        information. We are not responsible for the accuracy,
                        completeness, or legality of content entered by users.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        4. Offline Use and Data Syncing
                      </SoftTypography>
                      <SoftTypography paragraph>
                        The App allows offline data entry. Data is synced when
                        an internet connection is available. You are responsible
                        for ensuring your device properly syncs and that all
                        critical data is uploaded successfully.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        5. Intellectual Property
                      </SoftTypography>
                      <SoftTypography paragraph>
                        The CC&I Field Reporting App, including its specific
                        design, features, and source code, is owned by CC&I upon
                        completion and delivery of the project, subject to the
                        terms of the agreement between CC&I and G2 Web
                        Development LLC. G2 Web Development LLC retains
                        ownership of any general frameworks, libraries, or
                        reusable components developed independently of the
                        CC&I-specific implementation. Users may not copy,
                        modify, distribute, or create derivative works from the
                        CC&I Field Reporting App without the express written
                        consent of CC&I.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        6. Disclaimer of Warranties
                      </SoftTypography>
                      <SoftTypography paragraph>
                        The App is provided “as is” without warranties of any
                        kind, express or implied. We do not guarantee that the
                        App will be error-free, uninterrupted, or fully secure.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        7. Limitation of Liability
                      </SoftTypography>
                      <SoftTypography paragraph>
                        To the maximum extent permitted by law, G2 Web
                        Development LLC shall not be liable for any indirect,
                        incidental, special, or consequential damages arising
                        out of your use of or inability to use the App.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        8. Updates and Modifications
                      </SoftTypography>
                      <SoftTypography paragraph>
                        We may update or modify the App and these Terms at any
                        time. Continued use of the App after changes are posted
                        constitutes your acceptance of the updated Terms.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        9. Termination
                      </SoftTypography>
                      <SoftTypography paragraph>
                        We reserve the right to suspend or terminate access to
                        the App for violations of these Terms or for any other
                        reason at our sole discretion.
                      </SoftTypography>

                      <SoftTypography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        10. Contact Information
                      </SoftTypography>
                      <SoftTypography paragraph>
                        G2 Web Development LLC
                        <br />
                        Email:{" "}
                        <Link href="mailto:info@g2webdevelopment.com">
                          info@g2webdevelopment.com
                        </Link>
                        <br />
                        Phone: +1 (864) 561-5380
                        <br />
                        Website:{" "}
                        <Link
                          href="https://g2webdevelopment.com"
                          target="_blank"
                          rel="noopener"
                        >
                          https://g2webdevelopment.com
                        </Link>
                      </SoftTypography>

                      <SoftTypography
                        variant="body2"
                        color="textSecondary"
                        mt={4}
                      >
                        © 2025 G2 Web Development LLC. All rights reserved.
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              )}
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

export default Settings;
